import React, { useEffect, useState, useRef } from "react";
import CustomTable from "../../components/Table";
import Header from "../../components/Header";
import { OPERTIONS_URL } from "../../common/common";
import AdminCointainer from "../../containers/Admin";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/AddButton";
import { Plus, client } from "../../common/svg";
import { ApiService } from "../../service/api.service";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import ToggleActive from "../../components/ToggleSwitch/ToggleActive";

type ClientData = {
  clientName: string;
  companyName: string;
  gstNumber: string;
  address: string;
  contactDetails: string;
  clientId?: string;
  isActive: boolean;
  companyType?: string; // Added companyType to match the dataKey
};

type Column<T> = {
  header: string;
  dataKey: keyof T;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
  align?: "left" | "center" | "right";
};

const ClientPage = () => {
  const navigate = useNavigate();
  const apiService = new ApiService();
  const [clientData, setClientData] = useState<ClientData[]>([]);
  const [search, setSearch] = useState<string>("");
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const entityId = localStorage.getItem('entity_id');
  const menuRef = useRef<HTMLDivElement>(null);
  // const { showToast } = useToast();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const[active, setActive] = useState<boolean>(true);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggleActive = async (clientId: string, isActive: boolean) => {
    try {
      const response: any = await apiService.putApi(
        `${OPERTIONS_URL}/client/status-route`,
        {
          clientId: clientId,
          isActive: isActive ? 1 : 0
        }
      );
      
      if (response.status === "200") {
        getClientData()
        setClientData(prevData => 
          prevData.map(client => 
            client.clientId === clientId ? { ...client, isActive } : client
          )
        );
      }
    } catch (error) {
      console.error("Error updating client status:", error);
    }
  };

  const columns: Column<ClientData>[] = [
    { header: "Company Name", dataKey: "companyName" },
    { header: "GST Number", dataKey: "gstNumber" },
    {
      header: "Company Type",
      dataKey: "companyType",
      render: (value: string | boolean | undefined) => (
     <div className="text-left max-w-[200px] whitespace-pre-wrap break-words">
          {typeof value === 'string' ? value.split("\n").map((line, i) => (
            <div key={i}>{line}</div>
          )) : null}
        </div>
      ),
    },
    {
      header: "Contact Details",
      dataKey: "contactDetails",
      render: (value: string | boolean | undefined) => (
        <div className="text-sm text-left leading-relaxed">
          {typeof value === 'string' ? value.split("\n").map((line, i) => (
            <div key={i}>{line}</div>
          )) : null}
        </div>
      ),
    },
    {
      header: "Status",
      dataKey: "isActive",
      render: (value: string | boolean | undefined) => {
        let status: string | null = null;
    
        if (typeof value === "boolean") {
          status = value ? "Active" : "Inactive";
        } else if (typeof value === "string") {
          if (value === "1") {
            status = "Active";
          } else if (value === "0") {
            status = "Inactive";
          } else {
            return (
              <div>
                {value
                  .split("\n")
                  .map((line, i) => <div key={i}>{line}</div>)}
              </div>
            );
          }
        }
    
        return <div>{status}</div>;
      }
    },    
    {
      header: "Actions",
      dataKey: "clientId",
      align: "center",
      render: (value, row) => (
        <div
          className="relative ml-2"
          ref={activeMenu === value ? menuRef : null}
          onClick={(e) => e.stopPropagation()} // Prevent row click when interacting with menu
        >
          <button 
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation();
              setActiveMenu(activeMenu === value ? null : (value as string | null));
            }}
          >
            <div className="flex flex-col items-center justify-center h-5">
              <div className="w-1 h-1 bg-gray-600 rounded-full mb-0.5"></div>
              <div className="w-1 h-1 bg-gray-600 rounded-full mb-0.5"></div>
              <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
            </div>
          </button>
    
          {/* Dropdown Menu */}
          {activeMenu === value && (
            <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 py-1 border border-gray-200"
                 onClick={(e) => e.stopPropagation()}
            >
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row);
                  setActiveMenu(null);
                }}
              >
                Edit
              </button>
    
              <div className="px-4 py-2">
                <ToggleSwitch
                  showLabel={true}
                  checked={row.isActive}
                  onChange={(isActive) => {
                    handleToggleActive(row.clientId as string, isActive);
                    setActiveMenu(null);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      ),
    }
  ];

  const handleEdit = (item: ClientData) => {
    navigate(`edit/${item.clientId}`);
  };

  const handleDelete = (item: ClientData) => {
    if (window.confirm(`Are you sure you want to delete ${item.companyName}?`)) {
      deleteClient(item.clientId as string);
    }
  };

  const deleteClient = async (clientId: string) => {
    try {
      const response: any = await apiService.deleteAPi(`/client/delete/${clientId}`);
      if (response.status === "200") {
        getClientData();
      }
    } catch (error) {
      console.error("Error deleting client:", error);
    }
  };

  const getClientData = async (isLoadingMore = false) => {
    // Only fetch if there's more data to load
    if (!hasMoreData && isLoadingMore) return;
  
    setLoading(true);
    try {
      const response: any = await apiService.getApi(
        `${OPERTIONS_URL}/client/get-all-route?page=${currentPage}&limit=${pageSize}&search=${search}&entityId=${entityId}&status=${active?1:0}`
      );
      
      if (response.status === "200") {
        const formattedData = response.body.data.map((client: any) => ({
          companyName: client.firstName || "N/A", 
          gstNumber: client.gstNumber,
          companyType: client.companyTypes.map((item: any) => item.value).join(","),
          address: `${client.address1}${client.address2 ? ", " + client.address2 : ""}, ${client.city}, ${client.state}, ${client.country}, ${client.pincode}`,
          contactDetails: `${client.phoneNumber}\n${client.emailId}`,
          clientId: client?.clientId,
          isActive: client?.isActive === 1 // Convert 1/0 to true/false
        }));
        
        // Determine if there's more data
        const totalRecords = parseInt(response.body.pagination?.totalRecords) || 0;
        const currentLoadedItems = isLoadingMore 
          ? clientData.length + formattedData.length 
          : formattedData.length;
        
        // Update has more data flag
        setHasMoreData(currentLoadedItems < totalRecords);
        
        // For infinite scroll, append new data if loading more
        setClientData(prevData => 
          isLoadingMore ? [...prevData, ...formattedData] : formattedData
        );
        
        setTotalItems(totalRecords);
      }
    } catch (error) {
      console.error("Error fetching client data:", error);
      setHasMoreData(false);
    } finally {
      setLoading(false);
    }
  };
  
  const handleLoadMore = () => {
    // Only load more if there's more data
    if (hasMoreData) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      getClientData(true);
    }
  };
  
  useEffect(() => {
    // Reset has more data and fetch initial data when search or entity changes
    setHasMoreData(true);
    setCurrentPage(1);
    getClientData();
  }, [search, entityId,active]);

  // const handleRowClick = (item: ClientData) => {
  //   navigate(`view/${item.clientId}`);
  // };
  const handleRowClick = (item: ClientData) => {
    if (active && item.isActive) {
      navigate(`view/${item.clientId}`);
    }
  };

  const handleAdd = () => {
    navigate("add");
  };

  return (
    <div className="">
   <Header
  icon={client}
  title="Client"
  toggleComponent={
    <div className="flex items-end gap-2 ml-[450px]">
      <ToggleActive
        onChange={(isActive: boolean) => {
          setActive(isActive); // Update the state
          console.log(isActive ? "active 1" : "inactive 0");
        }}
      />
    </div>
  }
  showSearch={true}
  setSearch={setSearch}
>
  <div>
    <CustomButton label="" onClick={handleAdd} icon={Plus} />
  </div>
</Header>


      <AdminCointainer>
        <CustomTable<ClientData>
          columns={columns}
          data={clientData}
          onRowClick={handleRowClick}
          loading={loading}
          onLoadMore={handleLoadMore}
        />
      </AdminCointainer>
    </div>
  );
};

export default ClientPage;